import React, { useLayoutEffect, useRef, useState } from "react";
import { PetHeaderRenderer } from "@/features/petHeader/PetHeaderRenderer";
import { StickerInteractionLayer } from "@/features/petHeader/StickerInteractionLayer";
import {
  addCatalogSticker,
  replaceEditedSticker,
} from "@/features/petHeader/stickerEditing";
import type { PetHeaderSticker } from "@/features/petHeader/types";
import { containDemoSticker, demoCatalog, windowDemo } from "./demo";
import type { DemoGeometry } from "./demo";
import cls from "./styles.module.scss";
import { useNearViewport } from "@/hooks/useNearViewport";
import { DeferredImage } from "@/ui/DeferredImage/DeferredImage";

export function HomepageMemoryWindow() {
  const [sceneRef, nearby] = useNearViewport<HTMLDivElement>();
  const copyRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const [geometry, setGeometry] = useState<DemoGeometry>({
    viewport: { width: 1440, height: 640 },
    top: 260,
    bottom: 560,
  });
  const [stickers, setStickers] = useState<PetHeaderSticker[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hasMoved, setHasMoved] = useState(false);
  const [resetVersion, setResetVersion] = useState(0);
  const [message, setMessage] = useState("");

  useLayoutEffect(() => {
    const scene = sceneRef.current,
      copy = copyRef.current,
      footer = footerRef.current;
    if (!scene || !copy || !footer) return;
    let connected = true;
    const measure = () => {
      if (!connected) return;
      const bounds = scene.getBoundingClientRect();
      if (bounds.width <= 0 || bounds.height <= 0) return;
      const next: DemoGeometry = {
        viewport: { width: bounds.width, height: bounds.height },
        top: copy.getBoundingClientRect().bottom - bounds.top + 16,
        bottom: footer.getBoundingClientRect().top - bounds.top - 16,
      };
      setGeometry(next);
      setStickers((current) =>
        current.map((item) => containDemoSticker(item, next))
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    for (const node of [scene, copy, footer]) observer.observe(node);
    return () => {
      connected = false;
      observer.disconnect();
    };
  }, []);

  const next = demoCatalog.find(
    (item) => !stickers.some((sticker) => sticker.assetId === item.asset.key)
  );
  const composition = { ...windowDemo, stickers };
  const mode = geometry.viewport.width <= 760 ? "mobile" : "desktop";
  const showHint = stickers.length > 0 && !hasMoved;

  const add = () => {
    if (!next) return;
    setStickers((current) =>
      addCatalogSticker(current, next).map((item) =>
        containDemoSticker(item, geometry)
      )
    );
    setSelectedId(`draft:${next.asset.key}`);
    setMessage(
      `${next.asset.name} в окне. Перетащи стикер мышью или пальцем. Можно двигать и стрелками на клавиатуре.`
    );
  };
  const change = (sticker: PetHeaderSticker) => {
    setStickers((current) =>
      replaceEditedSticker(current, containDemoSticker(sticker, geometry))
    );
    setSelectedId(sticker.id);
    setHasMoved(true);
  };
  const remove = (id: string) => {
    setStickers((current) => current.filter((item) => item.id !== id));
    setSelectedId(null);
    setResetVersion((value) => value + 1);
    setMessage("Стикер убран из примера. Его можно добавить снова.");
    addRef.current?.focus({ preventScroll: true });
  };
  const reset = () => {
    setStickers([]);
    setSelectedId(null);
    setHasMoved(false);
    // Remount the interaction layer to release any unfinished pointer drag.
    setResetVersion((value) => value + 1);
    setMessage("Пример очищен. Можно снова поместить котика и собачку.");
    addRef.current?.focus({ preventScroll: true });
  };

  return (
    <div
      className={cls.scene}
      ref={sceneRef}
      data-home-window-demo
      onPointerDown={() => setSelectedId(null)}
    >
      {nearby && (
        <PetHeaderRenderer
          transformMode="2d"
          composition={composition}
          viewportMode={mode}
          viewportSize={geometry.viewport}
          interactivePlants={false}
        />
      )}
      <div className={cls.shade} aria-hidden="true" />
      <div className={cls.copy} ref={copyRef} data-window-copy>
        <h2 id="how-title">Место, где живёт твоя история</h2>
        <button
          ref={addRef}
          type="button"
          className={cls.addButton}
          disabled={!next}
          onClick={add}
          aria-describedby="window-demo-instructions"
          title={next ? `Добавить: ${next.asset.name}` : undefined}
        >
          {next ? (
            <DeferredImage
              src={next.asset.imageUrl}
              alt=""
              draggable={false}
              decoding="async"
            />
          ) : (
            <span aria-hidden="true">✓</span>
          )}
          {next ? "Поместить стикер" : "Стикеры добавлены"}
        </button>
        <p className={cls.hint} data-visible={showHint} aria-hidden="true">
          Перетащи стикер — собери свой маленький мир
        </p>
      </div>
      <p id="window-demo-instructions" className={cls.visuallyHidden}>
        Это пример окна памяти. Первая кнопка добавляет котика, вторая —
        собачку. Выдели стикер и двигай мышью, пальцем или стрелками; Shift
        ускоряет перемещение. За угловой маркер можно изменить размер. Delete
        удаляет выделенный стикер, Escape снимает выделение. Настоящая страница
        и запас света не меняются.
      </p>
      <div
        className={cls.interactions}
        role="group"
        aria-label="Стикеры в примере окна памяти"
        aria-describedby="window-demo-instructions"
      >
        <StickerInteractionLayer
          key={resetVersion}
          composition={composition}
          mode={mode}
          viewport={geometry.viewport}
          overflow={false}
          catalog={demoCatalog}
          selectedId={selectedId}
          disabled={false}
          onSelect={setSelectedId}
          onChange={change}
          onDelete={remove}
        />
      </div>
      <div className={cls.footer} ref={footerRef} data-window-footer>
        <span>Окно памяти · пример</span>
        <button
          type="button"
          className={cls.resetButton}
          onClick={reset}
          disabled={stickers.length === 0}
        >
          Очистить пример
        </button>
      </div>
      <p className={cls.visuallyHidden} role="status">
        {message}
      </p>
    </div>
  );
}
